/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  ChecklistElementType,
  CheckUsage,
  ChecklistExportToOfficeDto,
  ChecklistImportAvailabilityDto,
  ChecklistImportFromMasterZoneDto,
  MasterZoneRestService,
  MobileChecklistsRestService,
  OfficeRestService,
} from '@gh/builder-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DictionaryService,
  formStateSnapshot,
  getRouteParam,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';

import { Observable } from 'rxjs';

import { MasterZone } from '../../offices/resources/master-zone.model';
import { OfficeListItem } from '../../offices/resources/office-list-item.model';

import {
  ChecklistDetails,
  ChecklistItem, newChecklistDetails,
  chklDetailsFromDto, toClonedChklDetails, ChklSection, ChklClause, chklDetailsToDto,
} from './checklists.model';

import { map$, switchMap$ } from '@gh/rx/operators';
import { ActivatedRouteSnapshot } from '@angular/router';

import { uniqueId } from 'lodash';
import { ComponentListCheckUsage } from '../../estimating/resources/component-list-item.model';
import { of$ } from '@gh/rx';

@Injectable()
export class ChecklistsBusinessService implements InitViewService {
  constructor(private checklistRest: MobileChecklistsRestService,
              private translations: TranslationService,
              private dictionaryService: DictionaryService,
              private userContext: BuilderUserContext,
              private officeRest: OfficeRestService,
              private masterZoneRest: MasterZoneRestService) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      checklist: this.findAll(false),
      masterZones: this.userContext.corporateView
        ? this.masterZoneRest.findAllActive()
        : of$(<MasterZone[]>[]),
    };
  }

  findOfficesByName(query: FilteredDataQuery<string>, masterZoneId?: ID): Observable<OfficeListItem[]> {
    return this.officeRest.findAllAsAutocompleteDataSource(
      <string>query.filter,
      masterZoneId,
      ['ACTIVE', 'NEW', 'BLOCKED'],
    )
      .select(query)
      .pipe(map$(getSlicedData));
  }

  getImportAvailability(officeId: ID): Observable<ChecklistImportAvailabilityDto> {
    return this.checklistRest.getImportAvailability(officeId);
  }

  getExportAvailability(masterZoneId: ID): Observable<ChecklistImportAvailabilityDto> {
    return this.checklistRest.getExportAvailability(masterZoneId);
  }

  exportToOffice(payload: ChecklistExportToOfficeDto): Observable<void> {
    return this.checklistRest.exportToOffice(payload);
  }

  importFromMasterZone(payload: ChecklistImportFromMasterZoneDto): Observable<void> {
    return this.checklistRest.importFromMasterZone(payload);
  }

  initDetailsView(route: ActivatedRouteSnapshot): any {
    const itemId = getRouteParam(route, 'id');
    return {
      details: isRouteForCreationMode(route)
        ? newChecklistDetails(this.defineLevel()) : this.get(itemId),
      elementTypeDict: this.dictionaryService.getByEnum(ChecklistElementType),
    };

  }

  updateOrder(checkLIst: ChecklistItem[]): Observable<ChecklistItem[]> {
    return this.checklistRest.updateOrder(checkLIst);
  }
  delete(id: ID): Observable<ChecklistItem> {
    return this.checklistRest.delete(id);
  }
  undelete(id: ID): Observable<ChecklistItem> {
    return this.checklistRest.undelete(id);
  }
  checkUsage(component: ChecklistItem): Observable<CheckUsage> {
    return this.checklistRest.checkUsage(component);
  }
  deactivateOrDelete(component: ChecklistItem, checkUsage: ComponentListCheckUsage): Observable<any> {
    return this.checklistRest.deactivateOrDelete(component, checkUsage);
  }

  findAll(showDeleted: boolean): Observable<ChecklistItem[]> {
    return this.checklistRest.findAll(showDeleted);
  }

  get(id: ID): Observable<ChecklistDetails> {
    return this.checklistRest.get(id).pipe(switchMap$(
      (checklist) => checklist.parentId ?
         this.checklistRest.get(checklist.parentId)
           .pipe(map$((parentCheckList) => ({
             ...chklDetailsFromDto(checklist),
             isParentEditable: !parentCheckList.notEditable,
           }))) : of$(chklDetailsFromDto(checklist)),
    ));
  }
  clone(id: ID): Observable<ChecklistDetails> {
    return this.checklistRest.get(id).pipe(
      map$(chklDetailsFromDto),
      map$(toClonedChklDetails));
  }

  save(details: ChecklistDetails): Observable<ChecklistDetails> {
    if (details.id) {
      return this.checklistRest.update(chklDetailsToDto(details)).pipe(map$(chklDetailsFromDto));
    } else {
      return this.checklistRest.create(chklDetailsToDto(details)).pipe(map$(chklDetailsFromDto));
    }
  }

  createNewSection(): ChklSection {
    return {
      id: uniqueId('chkl-section:'),
      info: formStateSnapshot({
        title: this.translations.get('lbl_new_element'),
        elementType: undefined,
      }),
      checklistItems: [],
      isNew: true,
    };
  }
  createNewClause(): ChklClause {
    return {
      id: uniqueId('chkl-clause:'),
      title:  this.translations.get('lbl_new_item'),
    };
  }

  get officeView(): boolean {
    return this.userContext.officeView;
  }

  private defineLevel(): number {
    if (this.userContext.corporateView) {
      return 0;
    } else if (this.userContext.masterView) {
      return 1;
    } else {
      return 2;
    }
  }
}
